// Canonical, reusable filter criteria for narrowing students / frequentations.
// One JSON-serializable shape travels end to end (see backend
// Parcours.Core/Infrastructure/StudentFilterCriteria.cs). Used by the StudentFilter
// component and wired into the student & frequentation grids, exams, and reports.

export interface StudentFilterCriteria {
  nameContains?: string;          // matches LastName OR FirstName (case-insensitive contains)
  emailContains?: string;
  recordNumberContains?: string;
  ageMin?: number;                // inclusive, computed against birthdate
  ageMax?: number;                // inclusive
  birthDate?: string;             // exact match, YYYY-MM-DD
  pathTypes?: string[];           // frequentation enum codes (IN semantics)
  exemptionCodes?: string[];
  fundingSources?: string[];
}

export function emptyStudentFilter(): StudentFilterCriteria {
  return {
    nameContains: '',
    emailContains: '',
    recordNumberContains: '',
    ageMin: undefined,
    ageMax: undefined,
    birthDate: undefined,
    pathTypes: [],
    exemptionCodes: [],
    fundingSources: [],
  };
}

function hasText(v: string | undefined | null): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

/** Number of active (populated) criteria — drives the button badge count. */
export function studentFilterCount(c: StudentFilterCriteria | null | undefined): number {
  if (!c) return 0;
  let n = 0;
  if (hasText(c.nameContains)) n++;
  if (hasText(c.emailContains)) n++;
  if (hasText(c.recordNumberContains)) n++;
  // age range counts as a single criterion when either bound is set
  if (c.ageMin != null || c.ageMax != null) n++;
  if (hasText(c.birthDate)) n++;
  if (c.pathTypes?.length) n++;
  if (c.exemptionCodes?.length) n++;
  if (c.fundingSources?.length) n++;
  return n;
}

export function isStudentFilterEmpty(c: StudentFilterCriteria | null | undefined): boolean {
  return studentFilterCount(c) === 0;
}

/**
 * Build a clean, minimal criteria object containing only populated fields,
 * or null when the filter is empty. This is what goes over the wire.
 */
export function normalizeStudentFilter(
  c: StudentFilterCriteria | null | undefined
): StudentFilterCriteria | null {
  if (isStudentFilterEmpty(c) || !c) return null;
  const out: StudentFilterCriteria = {};
  if (hasText(c.nameContains)) out.nameContains = c.nameContains!.trim();
  if (hasText(c.emailContains)) out.emailContains = c.emailContains!.trim();
  if (hasText(c.recordNumberContains)) out.recordNumberContains = c.recordNumberContains!.trim();
  if (c.ageMin != null) out.ageMin = c.ageMin;
  if (c.ageMax != null) out.ageMax = c.ageMax;
  if (hasText(c.birthDate)) out.birthDate = c.birthDate!;
  if (c.pathTypes?.length) out.pathTypes = [...c.pathTypes];
  if (c.exemptionCodes?.length) out.exemptionCodes = [...c.exemptionCodes];
  if (c.fundingSources?.length) out.fundingSources = [...c.fundingSources];
  return out;
}

/** JSON string for transport, or null when no criteria are set. */
export function serializeStudentFilter(
  c: StudentFilterCriteria | null | undefined
): string | null {
  const normalized = normalizeStudentFilter(c);
  return normalized ? JSON.stringify(normalized) : null;
}
